/**
 * The product's one tab control: a pill track at the density tier's control
 * height, a white pill on the current tab and a muted count after each label.
 * The admin console (Support's header views, the inbox's status filter, the
 * knowledge list's kind filter, every Accounting tab row) and the customer
 * app's accounting pages (Transactions, Reports, Accounts, the transaction
 * drawer) are this component, so the two apps look and behave alike.
 *
 * NOTHING MOVES WHEN DATA ARRIVES. A counted tab reserves its count's slot
 * before the number is known, and a tab with an icon swaps its label for the
 * icon below 640px by CSS alone — so a tab row is the same width on its first
 * paint and after every fetch.
 *
 * Built straight on Base UI (the headless library both apps already run) and
 * the tab and menu recipes, so neither app's own `Tabs` wrapper is needed.
 */
import { Menu } from "@base-ui/react/menu";
import { Tabs } from "@base-ui/react/tabs";
import type { ComponentType, ReactNode } from "react";

import { cn } from "./cn";
import { menuRecipe, tabsRecipe } from "./recipes";

export interface PillTabItem {
  value: string;
  label: string;
  /** True where the tab carries a count: its slot is reserved while the number is unknown. */
  counted?: boolean;
  /** Shown after the label once known; zero included. */
  count?: number;
  /**
   * The count is known at first paint and drawn only when something waits:
   * no slot is reserved, so no gap follows a one-digit count.
   */
  fit?: boolean;
  /** Goes on the count, for tests. */
  testId?: string;
  /** Shown instead of the label below 640px; the label stays for screen readers. */
  icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
}

export interface PillTabsProps {
  items: readonly PillTabItem[];
  /**
   * Overflow tabs, in a menu at the end of the track. While one is open the
   * menu's trigger names it and wears the current pill.
   */
  more?: readonly PillTabItem[];
  /** The menu trigger's text while no overflow tab is open. */
  moreLabel?: string;
  value: string;
  onChange: (value: string) => void;
  /** The pointer or focus reached a tab: a chance to load it before the click. */
  onIntent?: (value: string) => void;
  /** Each tab's `data-testid` is `<prefix>-<value>`; the menu's is `<prefix>-more`. */
  testIdPrefix?: string;
  /**
   * Set when the caller draws its own panels: each tab gets
   * `id="<idPrefix>-tab-<value>"` and `aria-controls="<idPrefix>-panel-<value>"`,
   * and the caller gives its panel that id with `aria-labelledby` back.
   */
  idPrefix?: string;
  className?: string;
  "aria-label": string;
}

const chevronDown = (
  <svg
    aria-hidden="true"
    viewBox="0 0 16 16"
    className="size-3.5 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m4 6 4 4 4-4" />
  </svg>
);

const ellipsis = (
  <svg
    aria-hidden="true"
    viewBox="0 0 16 16"
    className="size-4 shrink-0 sm:hidden"
    fill="currentColor"
  >
    <circle cx="3" cy="8" r="1.25" />
    <circle cx="8" cy="8" r="1.25" />
    <circle cx="13" cy="8" r="1.25" />
  </svg>
);

/** The ids `idPrefix` wires, for a caller's panel. */
export const pillTabId = (idPrefix: string, value: string) =>
  `${idPrefix}-tab-${value}`;
export const pillPanelId = (idPrefix: string, value: string) =>
  `${idPrefix}-panel-${value}`;

/**
 * The panel under a PillTabs row for a caller that draws one panel at a
 * time: the tab recipe's content look (text-sm, the row's gap above), wired
 * to its tab by id. Render it for the open value only.
 */
export function PillTabPanel({
  idPrefix,
  value,
  className,
  children,
}: {
  idPrefix: string;
  value: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      // In the tab order even with no focusable child (a table of figures),
      // as the headless panel it stands in for was.
      tabIndex={0}
      data-slot="tabs-content"
      id={pillPanelId(idPrefix, value)}
      aria-labelledby={pillTabId(idPrefix, value)}
      className={cn(tabsRecipe.content, "mt-2", className)}
    >
      {children}
    </div>
  );
}

export function PillTabs({
  items,
  more = [],
  moreLabel = "More",
  value,
  onChange,
  onIntent,
  testIdPrefix = "pill-tab",
  idPrefix,
  className,
  "aria-label": ariaLabel,
}: PillTabsProps) {
  const openMore = more.find((item) => item.value === value);
  const triggers = items.map((item) => (
    <Tabs.Tab
      key={item.value}
      value={item.value}
      data-slot="tabs-trigger"
      data-testid={`${testIdPrefix}-${item.value}`}
      id={idPrefix ? pillTabId(idPrefix, item.value) : undefined}
      // Only the open panel exists in the DOM, so only the selected tab names one.
      aria-controls={
        idPrefix && item.value === value
          ? pillPanelId(idPrefix, item.value)
          : undefined
      }
      className={tabsRecipe.trigger}
      onPointerEnter={() => onIntent?.(item.value)}
      onFocus={() => onIntent?.(item.value)}
    >
      {item.icon ? (
        <>
          <item.icon className="sm:hidden" aria-hidden />
          {/* `not-sr-only` resets white-space to normal, so the label keeps
              the trigger's nowrap on a span of its own. */}
          <span className="sr-only sm:not-sr-only">
            <span className="whitespace-nowrap">{item.label}</span>
          </span>
        </>
      ) : (
        item.label
      )}
      {item.counted || item.count !== undefined ? (
        <span
          data-testid={item.testId}
          // Three digits wide before the number arrives, so the counts
          // landing does not push the tabs (and the filters after them)
          // sideways.
          // The class string is unchanged for a reserved slot; screens' tests read it.
          className={cn(
            "inline-block",
            !item.fit && "min-w-[3ch]",
            "text-left text-compact text-muted-foreground tabular-nums",
          )}
        >
          {item.count?.toLocaleString()}
        </span>
      ) : null}
    </Tabs.Tab>
  ));
  const children =
    more.length === 0
      ? triggers
      : [
          ...triggers,
          <Menu.Root key="__more">
            <Menu.Trigger
              data-slot="dropdown-menu-trigger"
              data-testid={`${testIdPrefix}-more`}
              data-active={openMore ? "" : undefined}
              aria-current={openMore ? "page" : undefined}
              // A menu button, not a tab, wearing the tab's own recipe (its
              // `data-active` look included) so the track reads as one row.
              className={cn(tabsRecipe.trigger, "flex-none gap-1")}
            >
              {ellipsis}
              <span className="sr-only sm:not-sr-only">
                {openMore ? openMore.label : moreLabel}
              </span>
              {chevronDown}
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Positioner
                className="isolate z-50 outline-none"
                align="end"
                side="bottom"
                sideOffset={4}
              >
                <Menu.Popup
                  data-slot="dropdown-menu-content"
                  className={cn(
                    menuRecipe.content,
                    "max-h-(--available-height) w-52 data-closed:overflow-hidden",
                  )}
                >
                  {more.map((item) => (
                    <Menu.Item
                      key={item.value}
                      data-slot="dropdown-menu-item"
                      data-testid={`${testIdPrefix}-${item.value}`}
                      className={menuRecipe.item}
                      onClick={() => onChange(item.value)}
                    >
                      {item.label}
                    </Menu.Item>
                  ))}
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>,
        ];

  return (
    <Tabs.Root
      data-slot="tabs"
      data-orientation="horizontal"
      data-horizontal=""
      // No tab is current while an overflow tab is open.
      value={openMore ? null : value}
      onValueChange={(next) => onChange(String(next))}
      className={cn(
        "group/tabs flex min-w-0 max-w-full flex-col gap-2",
        className,
      )}
    >
      <Tabs.List
        data-slot="tabs-list"
        data-variant="default"
        aria-label={ariaLabel}
        className={cn(
          tabsRecipe.list.base,
          tabsRecipe.list.default,
          "max-w-full justify-start overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        )}
      >
        {children}
      </Tabs.List>
    </Tabs.Root>
  );
}
